import { Suspense, lazy, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthForm } from "@/components/auth/AuthForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";

const EtherealBeamsHero = lazy(
  () => import("@/components/auth/EtherealBeamsHero"),
);

/**
 * SPEC-16 §8 — landing + auth composition (modal variant).
 *
 * The hero fills the viewport and never scrolls away; clicking one of
 * its CTA buttons opens a shadcn `Dialog` that floats the AuthForm on
 * top, with the correct tab pre-selected. The sliding login↔signup
 * animation stays inside the dialog. There is no second-screen
 * section.
 *
 * three.js / @react-three/fiber stay in their own lazy chunk so the
 * main app bundle never carries them; the fallback is a plain black
 * screen so the page does not flash light during code-split load.
 */
export function LoginPage() {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const openWith = (m: "login" | "signup"): void => {
    setMode(m);
    setOpen(true);
  };

  return (
    <div className="relative min-h-screen w-full bg-black">
      <Suspense
        fallback={<div className="min-h-screen w-full bg-black" />}
      >
        <EtherealBeamsHero
          onLogin={() => openWith("login")}
          onSignup={() => openWith("signup")}
        />
      </Suspense>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md border-0 bg-transparent p-0 shadow-none ring-0 sm:max-w-md"
          showCloseButton={false}
        >
          {/* Radix requires a DialogTitle for accessibility; AuthForm's
              own visible header is the source of truth, so this one is
              visually hidden. */}
          <DialogTitle className="sr-only">
            {mode === "login" ? "Giriş Yap" : "Kayıt Ol"}
          </DialogTitle>
          <AuthForm
            mode={mode}
            onModeChange={setMode}
            onAuthed={() => {
              setOpen(false);
              navigate("/");
            }}
          />
        </DialogContent>
      </Dialog>

      <Toaster richColors position="bottom-right" />
    </div>
  );
}

export default LoginPage;
